import { useEffect, useState } from "react";
import {
  fetchRitiriResi,
  giornoLungo,
  oggiIso,
  ritiroResoMancabile,
  STATI_ATTIVI,
  type Prenotazione,
} from "@/features/ritiri/api";
import { prettifyApiError } from "@/shared/api";
import { fetchOrderReturns, type ReturnRecord } from "../api";
import { PrenotaRitiroResoDialog } from "./PrenotaRitiroResoDialog";
import { AVVISO_ESITO_IGNOTO } from "./RitiroReso";

type Stato = { reso: ReturnRecord; ritiro: Prenotazione | null };

type Props = {
  orderReference: string;
  /** Il dialog del ritiro è aperto sopra quello del reso: chi lo ospita
   *  sospende Esc, o chiuderebbe il reso con la prenotazione in volo. */
  onAperto?: (aperto: boolean) => void;
};

/** In cima a «Crea reso»: se l'ordine ha già un reso con l'etichetta, la LDV
 *  ce l'ha il cliente e non si rifà. Da qui si prenota solo il ritiro, anche
 *  quando il corriere non è passato a quello prenotato. */
export function ResoEsistente({ orderReference, onAperto }: Props) {
  const [stato, setStato] = useState<Stato | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [aperto, setAperto] = useState(false);
  const [versione, setVersione] = useState(0);

  useEffect(() => {
    let attivo = true;
    (async () => {
      const resi = await fetchOrderReturns(orderReference);
      const reso = resi
        .filter((r) => r.kind === "reso" && r.status === "etichetta_pronta")
        .sort((a, b) => b.id - a.id)[0];
      if (!reso) {
        if (attivo) setStato(null);
        return;
      }
      const mappa = await fetchRitiriResi([reso.id]);
      if (attivo) setStato({ reso, ritiro: mappa[String(reso.id)] ?? null });
    })().catch((e: unknown) => {
      if (attivo) setErrore(prettifyApiError(e));
    });
    return () => {
      attivo = false;
    };
  }, [orderReference, versione]);

  function apri(valore: boolean) {
    setAperto(valore);
    onAperto?.(valore);
  }

  function chiudi() {
    apri(false);
    setVersione((v) => v + 1);
  }

  if (errore) {
    return (
      <p className="rounded-sm bg-red-soft px-3 py-2 text-[12.5px] text-ink">
        Non riesco a leggere i resi dell'ordine: {errore}
      </p>
    );
  }
  if (!stato) return null;

  const { reso, ritiro } = stato;
  const ldv = reso.tracking_numbers.join(", ") || "senza numero";
  const mancabile = ritiroResoMancabile(ritiro, oggiIso());
  const attivo = ritiro !== null && STATI_ATTIVI.includes(ritiro.stato);

  let riga: string;
  if (ritiro?.stato === "esito_ignoto") riga = AVVISO_ESITO_IGNOTO;
  else if (mancabile && ritiro)
    riga = `Ritiro di ${giornoLungo(ritiro.data_ritiro)}${ritiro.codice ? ` (codice ${ritiro.codice})` : ""}: se il corriere non è passato, prenotane un altro.`;
  else if (attivo && ritiro)
    riga = `Ritiro già prenotato ${giornoLungo(ritiro.data_ritiro)}${ritiro.codice ? ` · codice ${ritiro.codice}` : ""}.`;
  else riga = "Nessun ritiro attivo.";
  const puoPrenotare = !attivo || mancabile;

  return (
    <section
      aria-label="Reso già creato"
      className={`rounded-sm border px-3 py-2.5 ${puoPrenotare ? "border-amber bg-amber-soft" : "border-line bg-green-soft"}`}
    >
      <p className="text-[12.5px] font-bold text-ink">
        Reso già creato · LDV <span className="tabular-nums">{ldv}</span>
      </p>
      <p className="mt-0.5 text-[12.5px] text-ink">
        Il cliente ha già l'etichetta: non serve rifarla. {riga}
      </p>
      {puoPrenotare && (
        <button
          type="button"
          onClick={() => apri(true)}
          className="mt-2 min-h-[36px] rounded-sm border border-ink bg-ink px-3.5 py-2 text-[13px] font-bold text-on-ink hover:bg-ink/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
        >
          Prenota solo ritiro
        </button>
      )}
      {aperto && (
        <PrenotaRitiroResoDialog
          record={reso}
          mancato={mancabile ? ritiro : null}
          onChiudi={chiudi}
          onPrenotato={chiudi}
        />
      )}
    </section>
  );
}
